import { useCallback, useMemo, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import SocketService from '@src/services/SocketService';

import { useRoomContext } from '@src/contexts';
import { usePeersStore, useRoomActionsStore } from '@src/stores';

import { ECommonSocketKeys } from '@src/types';

const useParticipantsList = () => {
  const [hoveredRow, setHoveredRow] = useState('');
  const [isMenuPopperOpened, setIsMenuPopperOpened] = useState<Record<string, boolean>>({});

  const { participantsPeers, peerAction, currentPeer, canUserMute, isHostOrCohost } = useRoomContext();
  const { setRaiseHandSettings, raiseHandSettings, micSettings } = useRoomActionsStore(
    useShallow((state) => ({
      setRaiseHandSettings: state.setRaiseHandSettings,
      raiseHandSettings: state.raiseHandSettings,
      micSettings: state.micSettings,
    }))
  );
  const { raisedHandsQueue, peers, lowerHand } = usePeersStore(
    useShallow((state) => ({
      raisedHandsQueue: state.raisedHandsQueue,
      peers: state.peers,
      lowerHand: state.lowerHand,
    }))
  );

  const handleMouseEnter = useCallback((peerId: string) => {
    setHoveredRow(peerId);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredRow('');
  }, []);

  const handleMuteParticipant = useCallback(
    (peerId: string) => {
      if (peerId === currentPeer?.peer_info?.peer_id) {
        micSettings.onMicClick(!micSettings.isMicClosed);
        return;
      }

      const targetPeer = peers.get(peerId);
      const isMicOn = targetPeer?.peer_info?.peer_audio;

      if (isMicOn) {
        peerAction({ from_peer_name: currentPeer?.peer_name ?? '', id: peerId, action: 'mute', emit: true });
      }
    },
    [currentPeer, peerAction, micSettings, peers]
  );

  const raisedHandOrderMap = useMemo(() => {
    return new Map(raisedHandsQueue.map((peerId, index) => [peerId, index]));
  }, [raisedHandsQueue]);

  const participantsWithRaiseHand = useMemo(() => {
    return participantsPeers
      .map((participant) => {
        const peerId = participant.peer_info.peer_id;
        const index = raisedHandOrderMap.get(peerId);
        const handRaiseOrder = index !== undefined ? index + 1 : null;

        return { ...participant, handRaiseOrder };
      })
      .sort((a, b) => {
        if (a.handRaiseOrder && b.handRaiseOrder) {
          return a.handRaiseOrder - b.handRaiseOrder;
        }
        if (a.handRaiseOrder) return -1;
        if (b.handRaiseOrder) return 1;
        return 0;
      });
  }, [participantsPeers, raisedHandOrderMap]);

  const handleLowerAllRaisedHands = useCallback(() => {
    if (!currentPeer) return;

    const peerActionData = {
      action: 'lowerHandsAll',
      broadcast: true,
      peer_id: currentPeer?.peer_info?.peer_id,
    };

    SocketService.request(ECommonSocketKeys.PEER_ACTION, peerActionData).then((response) => {
      if (response?.success) {
        peers.forEach((peer) => {
          if (peer?.peer_info?.peer_hand) {
            lowerHand(peer.peer_info.peer_id);
          }
        });
        raiseHandSettings.isActive && setRaiseHandSettings({ isActive: false });
      }
    });
  }, [currentPeer, lowerHand, peers, raiseHandSettings.isActive, setRaiseHandSettings]);

  return {
    hoveredRow,
    participantsPeers: participantsWithRaiseHand,
    isMenuPopperOpened,
    currentPeer,
    canUserMute,
    handleMouseEnter,
    handleMouseLeave,
    handleMuteParticipant,
    setIsMenuPopperOpened,
    handleLowerAllRaisedHands,
    raisedHandsQueue,
    isHostOrCohost,
  };
};

export default useParticipantsList;
